import React, { useContext, useEffect, useState } from "react";
// import { useAuth, useFirebaseApp } from "reactfire"; // SEEMS TO CAUSE TYPE ERRORS.
import auth, { getAuth, User, createUserWithEmailAndPassword, signInWithEmailAndPassword, sendPasswordResetEmail, updateEmail as authUpdateEmail, updatePassword as authUpdatePassword, deleteUser as authDeleteUser, signInWithPopup, GoogleAuthProvider, OAuthProvider } from 'firebase/auth';
import { firebaseApp, functions } from "../App";
import { StudentEnrollmentFromServer } from "../../../data-types/Types";
import { httpsCallable } from "firebase/functions";

export const AuthContext = React.createContext({
  currentUser:null as User | null | undefined,
  signup:(email:string, password:string)=>{return null as Promise<auth.UserCredential>|null},
  login:(email:string, password:string)=>{return null as Promise<auth.UserCredential>|null},
  logout:()=>{return null as Promise<void>|null},
  resetPassword:(email:string)=>{return null as Promise<void>|null},
  updateEmail:(email:string)=>{return null as Promise<void>|null},
  updatePassword:(password:string)=>{return null as Promise<void>|null},
  deleteAccount:()=>{return null as Promise<void>|null},
  studentSignIns:[] as StudentEnrollmentFromServer[],
  createEnrollment:(classCode: string)=>{return {} as Promise<StudentEnrollmentFromServer>},
  logInStudent:(classCode: string, studentPassword: string) => {return {} as Promise<StudentEnrollmentFromServer>},
  signInWithGoogle: () => {return {} as Promise<auth.UserCredential>},
  signInWithMicrosoft: () => {return {} as Promise<auth.UserCredential>},
  isAdmin: false,
});

export function useAuth() {
  return useContext(AuthContext);
}

export const AuthProvider: React.FC = ({children, ...props}) => {
  const [currentUser, setCurrentUser] = useState<User | null>();
  const [loading, setLoading] = useState(true);
  const auth = getAuth(firebaseApp);

  const [studentSignIns, setStudentSignIns] = useState<StudentEnrollmentFromServer[]>(JSON.parse(localStorage.getItem("student-logins") || "[]") as StudentEnrollmentFromServer[]);
  const [isAdmin, setIsAdmin] = useState(false);

  function signup(email:string, password:string) {
    return createUserWithEmailAndPassword(auth, email, password);
  }

  function login(email:string, password:string) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  function logout(){
    setStudentSignIns([]);
    localStorage.clear();
    if (currentUser) {
      return auth.signOut();
    } else {
      const nonSignOut = async ()=>{}
      return nonSignOut();
    }
  }

  function resetPassword(email:string) {
    return sendPasswordResetEmail(auth, email);
  }

  function updateEmail(email:string) {
    if (!!currentUser){
      return authUpdateEmail(currentUser, email);
    }
    return null //Or null is to resolve type issues.
  }

  function updatePassword(password:string) {
    if (!!currentUser){
      return authUpdatePassword(currentUser, password);
    }
    return null //Or null is to resolve type issues.
  }

  function deleteAccount() {
    if (!!currentUser) {
      return authDeleteUser(currentUser);
    }
    return null;
  }

  const serverCreateStudentEnrollment = httpsCallable(functions, "createStudentEnrollment");
  async function createEnrollment(classCode:string) {
    return serverCreateStudentEnrollment({apiVersion: 1, classCode}).then((result)=>{
      const newSignIns = [...studentSignIns, result.data as StudentEnrollmentFromServer]
      setStudentSignIns(newSignIns);
      localStorage.setItem("student-logins", JSON.stringify(newSignIns));
      return result.data as StudentEnrollmentFromServer;
    }).catch((reason)=>{
      throw reason;
    })
  }

  const serverCheckStudentEnrollment = httpsCallable(functions, "checkStudentEnrollment");
  async function logInStudent(classCode:string, studentPassword:string) {
    return serverCheckStudentEnrollment({classCode, studentPassword}).then((result)=>{
      const newSignIns = [...studentSignIns, result.data as StudentEnrollmentFromServer]
      setStudentSignIns(newSignIns);
      localStorage.setItem("student-logins", JSON.stringify(newSignIns));
      return result.data as StudentEnrollmentFromServer;
    }).catch((reason)=>{
      throw reason;
    })
  }

  const googleProvider = new GoogleAuthProvider();
  const microsoftProvider = new OAuthProvider('microsoft.com');

  const signInWithGoogle = async () => {
    return signInWithPopup(auth, googleProvider)
      .catch((error) => {
        console.error(`Error Code: ${error.code} | Error Message: ${error.message}`);
        throw error;
      });
  };

  const signInWithMicrosoft = async () => {
    return signInWithPopup(auth, microsoftProvider)
      .catch((error) => {
        console.error(`Error Code: ${error.code} | Error Message: ${error.message}`);
        throw error;
      });
  };

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user: User | null) => {
      setCurrentUser(user);
      setLoading(false); // Enables waits to render until current user is loaded.
    })

    return unsubscribe;
  }, [auth])

  useEffect(() => {
    setIsAdmin(currentUser?.email === "sam@edapt.app");
  }, [currentUser]);

  return ( 
    <AuthContext.Provider value={{
      currentUser,
      signup,
      login,
      logout,
      resetPassword,
      updateEmail,
      updatePassword,
      deleteAccount,
      studentSignIns,
      createEnrollment,
      logInStudent,
      signInWithGoogle,
      signInWithMicrosoft,
      isAdmin,
    }}>
      {!loading? children: <>Loading...</>}
    </AuthContext.Provider>
   );
}

export default AuthProvider;